import {
    decodeLegacyColors,
    decodeLegacyRaster,
    encodeLegacyColors,
    encodeLegacyRaster,
    NO_LEGACY_COLOR,
} from "@/util/canvas/legacyRaster"

export const LAYER_EXTENSION_KEY = "dot-canvas.layers"
export const MAX_PAGE_LAYERS = 10
// 색 번호(index)·값(cssColor)은 모바일 캔버스 앱과 같다 - 0 검정 K · 1 파랑 B · 2 노랑 Y · 3 빨강 R.
// 앱(iOS·Android)은 레이어 칸의 colorIndex 를 이 번호로 읽고, graphic.colors 글자도 이 차례다.
// outlineColor: 출력 방식 "전체"에서 개체 테두리를 그 레이어 색으로 그릴 때 쓴다.
// 검정은 같은 색 점에 묻히므로 회색, 노랑은 흰 바탕에 안 보이므로 어둡게 낮췄다.
export const LAYER_COLOR_PALETTE = Object.freeze([
    Object.freeze({index: 0, key: "black", label: "검정", cssColor: "#000000", outlineColor: "rgba(110,110,110,0.85)"}),
    Object.freeze({index: 1, key: "blue", label: "파랑", cssColor: "#007aff", outlineColor: "rgba(0,122,255,0.85)"}),
    Object.freeze({index: 2, key: "yellow", label: "노랑", cssColor: "#ffcc00", outlineColor: "rgba(173,139,0,0.9)"}),
    Object.freeze({index: 3, key: "red", label: "빨강", cssColor: "#ff3b30", outlineColor: "rgba(255,59,48,0.85)"}),
])
/** 색 이름 → 번호. 번호를 숫자로 적지 않고 이름으로 찾는다. */
export const LAYER_COLOR_INDEX = Object.freeze(Object.fromEntries(LAYER_COLOR_PALETTE.map(color => [color.key, color.index])))
/*
 레이어 칸(colorOrder)에 적는 색 번호 차례. 자리 i 의 글자가 colorIndex i 의 색이다.
 2026-10-22 전 웹은 0 검정 · 1 빨강 · 2 파랑 · 3 노랑(KRBY)으로 적었고 colorOrder 칸이 없다. 그런 칸은 열 때 옮겨 읽는다.
 */
export const LAYER_COLOR_ORDER = "KBYR"
const LEGACY_WEB_COLOR_ORDER = "KRBY"
// 앱 색 꼴(graphic.colors)을 적는 면. 앱은 60x40 만 연다.
const LEGACY_COLOR_SURFACE = Object.freeze({width: 60, height: 40})

let layerSequence = 0
let objectSequence = 0

function createLayerId() {
    layerSequence += 1
    if (globalThis.crypto?.randomUUID) {
        return `layer-${globalThis.crypto.randomUUID()}`
    }
    return `layer-${Date.now().toString(36)}-${layerSequence.toString(36)}`
}

export function createEditorObjectId(prefix = "object") {
    objectSequence += 1
    if (globalThis.crypto?.randomUUID) {
        return `${prefix}-${globalThis.crypto.randomUUID()}`
    }
    return `${prefix}-${Date.now().toString(36)}-${objectSequence.toString(36)}`
}

function resolveSurface(page, fallbackSurface) {
    const width = page?.dotPadInfo?.width || fallbackSurface?.width
    const height = page?.dotPadInfo?.height || fallbackSurface?.height
    if (Number.isInteger(width) && width > 0 && Number.isInteger(height) && height > 0) {
        return {width, height}
    }

    const graphicLength = page?.graphic?.data?.length || 0
    return graphicLength === 6144 || graphicLength === 1536
        ? {width: 96, height: 64}
        : {width: 60, height: 40}
}

function normalizeRaster(raster, surface) {
    const pinCount = surface.width * surface.height
    if (typeof raster === "string") {
        try {
            return decodeLegacyRaster(raster, surface)
        } catch (error) {
            // 길이가 어긋난 hex는 빈 면으로 연다. dotPadInfo.totalCell이 없어 320/768을 잘못
            // 잡은 파일, 잘린 파일이 여기로 온다. 여기서 던지면 ensurePageLayerState가 실패해
            // loadPage 전체가 죽고 파일 자체를 못 연다. 레이어 도입 전에는 이런 페이지도
            // (그림이 깨질지언정) 열렸으므로 그 동작을 유지한다.
            console.warn("[layerState] 레거시 raster 해석 실패 - 빈 면으로 대체합니다", {
                actualHexLength: raster.length,
                expectedHexLength: pinCount / 4,
                reason: error.message,
            })
            return new Uint8Array(pinCount)
        }
    }
    if ((Array.isArray(raster) || raster instanceof Uint8Array) && raster.length === pinCount) {
        // Uint8Array.from(값변환) 은 이터레이터를 거쳐 같은 일을 수십 배 느리게 한다.
        // 여기는 레이어 합성마다 - 획 하나 뗄 때마다 레이어 수만큼 - 지나는 자리라 손으로 옮긴다.
        const normalized = new Uint8Array(pinCount)
        for (let index = 0; index < pinCount; index++) {
            normalized[index] = raster[index] ? 1 : 0
        }
        return normalized
    }
    return new Uint8Array(pinCount)
}

function normalizeByteArray(value) {
    if (value instanceof Uint8Array) return Uint8Array.from(value)
    if (Array.isArray(value)) return Uint8Array.from(value, byte => Number(byte) & 0xff)
    if (typeof value !== "string") return new Uint8Array()

    const hex = value.replace(/\s+/g, "")
    if (hex.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(hex)) return new Uint8Array()
    const bytes = new Uint8Array(hex.length / 2)
    for (let index = 0; index < bytes.length; index++) {
        bytes[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16)
    }
    return bytes
}

function encodeByteArray(bytes) {
    return Array.from(normalizeByteArray(bytes), byte => byte.toString(16).padStart(2, "0")).join("")
}

/*
 raster 를 켜진 핀의 인덱스 목록으로 적는다(인덱스당 hex 4자 = 2바이트).

 rasterHex 는 핀 하나에 1바이트를 쓰므로 넓은 상자에 점이 드문 객체 - 화면을 가로지르는
 펜 획, 대각선 - 에서 상자 전체를 값으로 싣는다. 실측(60x40): 대각선 획 5개가 21.6KB 였고
 그중 실제 점은 300개뿐이었다. 인덱스 목록으로는 1.2KB 다.

 점이 촘촘하면 반대로 인덱스 목록이 더 길다. 그래서 짧은 쪽을 골라 쓰고,
 고른 결과는 필드 이름(rasterHex/pinsHex)으로 알 수 있게 둔다.
 */
const PIN_INDEX_HEX_WIDTH = 4
const MAX_PIN_INDEX = 0xffff

/** 객체 raster 를 전부 rasterHex 로 적던 판. */
export const LAYER_SCHEMA_VERSION_BASE = 1
/** 희소한 객체 raster 를 pinsHex 로 적는 판. */
export const LAYER_SCHEMA_VERSION_PIN_INDEX = 2
const READABLE_LAYER_SCHEMA_VERSIONS = new Set([
    LAYER_SCHEMA_VERSION_BASE,
    LAYER_SCHEMA_VERSION_PIN_INDEX,
])

/** 인덱스 목록으로 적는 편이 짧은지. 0/1 이 아닌 값이 섞여 있으면 쓸 수 없다. */
function prefersPinIndexList(bytes, cellCount) {
    if (!Number.isInteger(cellCount) || cellCount <= 0) return false
    if (cellCount > MAX_PIN_INDEX + 1) return false
    if (!bytes || bytes.length !== cellCount) return false

    let lit = 0
    for (let index = 0; index < cellCount; index++) {
        const value = bytes[index]
        if (value !== 0 && value !== 1) return false
        if (value) lit++
    }
    // rasterHex 는 cellCount*2 자, 인덱스 목록은 lit*4 자. 같으면 호환되는 쪽을 남긴다.
    return lit * PIN_INDEX_HEX_WIDTH < cellCount * 2
}

function encodePinIndexList(bytes) {
    let hex = ""
    for (let index = 0; index < bytes.length; index++) {
        if (bytes[index]) hex += index.toString(16).padStart(PIN_INDEX_HEX_WIDTH, "0")
    }
    return hex
}

function decodePinIndexList(value, cellCount) {
    const raster = new Uint8Array(Math.max(0, cellCount))
    if (typeof value !== "string") return raster

    const hex = value.replace(/\s+/g, "")
    if (hex.length % PIN_INDEX_HEX_WIDTH !== 0 || !/^[0-9a-f]*$/i.test(hex)) return raster
    for (let cursor = 0; cursor < hex.length; cursor += PIN_INDEX_HEX_WIDTH) {
        const index = Number.parseInt(hex.slice(cursor, cursor + PIN_INDEX_HEX_WIDTH), 16)
        if (index < raster.length) raster[index] = 1
    }
    return raster
}

function cloneLayerObjectValue(value) {
    if (value instanceof Uint8Array) return Uint8Array.from(value)
    if (Array.isArray(value)) return value.map(cloneLayerObjectValue)
    if (!value || typeof value !== "object") return value

    const clone = {}
    for (const key of Object.keys(value)) {
        clone[key] = cloneLayerObjectValue(value[key])
    }
    return clone
}

function normalizeLayerObject(object) {
    if (!object || typeof object !== "object") return null
    // Page and layer duplication must detach nested object metadata from its source.
    // cells/raster are normalized again below to preserve their wire format.
    const normalized = cloneLayerObjectValue(object)
    if (object.type === "brailleText" || object.type === "brailleMath") {
        normalized.cells = normalizeByteArray(object.cellsHex ?? object.cells)
        delete normalized.cellsHex
    } else if (object.type === "tactileGraphic") {
        // pinsHex 는 상자 크기를 알아야 풀 수 있다. rasterHex 와 함께 오는 일은 없다.
        normalized.raster = typeof object.pinsHex === "string"
            ? decodePinIndexList(object.pinsHex, toCellCount(object))
            : normalizeByteArray(object.rasterHex ?? object.raster)
        delete normalized.rasterHex
        delete normalized.pinsHex
    }
    return normalized
}

/** 객체 상자가 담는 칸 수. pinsHex 를 풀 때 길이가 된다. */
function toCellCount(object) {
    const width = Number.isInteger(object?.width) ? object.width : 0
    const height = Number.isInteger(object?.height) ? object.height : 0
    return width > 0 && height > 0 ? width * height : 0
}

/*
 내보낼 한 벌은 살아 있는 객체와 실을 끊어 둔다.
 이 결과는 파일로만 가는 것이 아니라 페이지 클립보드(copyPage)에도 담긴다. 얕게 뜨면
 layout·extensions 같은 한 겹 아래가 원본과 같은 것이어서, 복사해 둔 뒤 원본 글을 고치면
 붙여넣을 내용까지 따라 바뀐다. 위쪽 값(x·cells)은 끊겨 있고 아래쪽만 붙어 있으면
 어느 쪽이 따라오는지 알 수 없어, 아예 끊지 않은 것보다 나쁘다.
 */
function serializeLayerObject(object) {
    if (!object || typeof object !== "object") return object
    if (object.type === "brailleText" || object.type === "brailleMath") {
        const serialized = cloneLayerObjectValue(object)
        serialized.cellsHex = encodeByteArray(object.cells)
        delete serialized.cells
        return serialized
    }
    if (object.type === "tactileGraphic") {
        const serialized = cloneLayerObjectValue(object)
        const raster = normalizeByteArray(object.raster)
        if (prefersPinIndexList(raster, toCellCount(object))) {
            serialized.pinsHex = encodePinIndexList(raster)
        } else {
            serialized.rasterHex = encodeByteArray(raster)
        }
        delete serialized.raster
        return serialized
    }
    return cloneLayerObjectValue(object)
}

function renderBrailleObject(target, object, surface) {
    const cells = normalizeByteArray(object?.cells)
    const lineLengths = Array.isArray(object?.cellLineLengths) ? object.cellLineLengths : []
    if (cells.length === 0 || lineLengths.length === 0) return

    const dots = object.dots === 8 ? 8 : 6
    const layout = object.layout || {}
    const letterSpacing = layout.letterSpacing === 0 ? 0 : 1
    const lineSpacing = Number.isInteger(layout.lineSpacing) ? Math.max(0, layout.lineSpacing) : 2
    const cellStep = letterSpacing === 0 ? 2 : 3
    const cellRows = dots / 2
    const lineStep = cellRows + lineSpacing
    const originX = Number.isInteger(object.x) ? object.x : 0
    const originY = Number.isInteger(object.y) ? object.y : 0
    let cellOffset = 0

    lineLengths.forEach((lineLength, lineIndex) => {
        const safeLength = Number.isInteger(lineLength) ? Math.max(0, lineLength) : 0
        for (let cellIndex = 0; cellIndex < safeLength && cellOffset < cells.length; cellIndex++, cellOffset++) {
            const cell = cells[cellOffset]
            for (let dotIndex = 0; dotIndex < dots; dotIndex++) {
                if ((cell & (1 << dotIndex)) === 0) continue
                const column = dotIndex >= cellRows ? 1 : 0
                const row = dotIndex % cellRows
                const x = originX + cellIndex * cellStep + column
                const y = originY + lineIndex * lineStep + row
                if (x < 0 || x >= surface.width || y < 0 || y >= surface.height) continue
                target[y * surface.width + x] = 1
            }
        }
    })
}

function renderTactileGraphicObject(target, object, surface) {
    const raster = normalizeByteArray(object?.raster)
    const width = Number.isInteger(object?.width) ? Math.max(0, object.width) : 0
    const height = Number.isInteger(object?.height) ? Math.max(0, object.height) : 0
    const originX = Number.isInteger(object?.x) ? object.x : 0
    const originY = Number.isInteger(object?.y) ? object.y : 0
    if (!width || !height || raster.length !== width * height) return

    for (let row = 0; row < height; row += 1) {
        const targetY = originY + row
        if (targetY < 0 || targetY >= surface.height) continue
        for (let column = 0; column < width; column += 1) {
            if (!raster[row * width + column]) continue
            const targetX = originX + column
            if (targetX < 0 || targetX >= surface.width) continue
            target[targetY * surface.width + targetX] = 1
        }
    }
}

export function composeLayerRaster(layer, fallbackSurface) {
    const surface = resolveSurface(null, fallbackSurface)
    const composite = normalizeRaster(layer?.raster, surface)
    ;(layer?.objects || []).forEach(object => {
        if (object?.type === "brailleText" || object?.type === "brailleMath") {
            renderBrailleObject(composite, object, surface)
        } else if (object?.type === "tactileGraphic") {
            renderTactileGraphicObject(composite, object, surface)
        }
    })
    return composite
}

export function normalizeLayerColorIndex(colorIndex) {
    return Number.isInteger(colorIndex) && colorIndex >= 0 && colorIndex < LAYER_COLOR_PALETTE.length
        ? colorIndex
        : 0
}

export function getLayerColor(colorIndex) {
    return LAYER_COLOR_PALETTE[normalizeLayerColorIndex(colorIndex)]
}

export const DEFAULT_LAYER_NAME = "기본"

/*
 첫 레이어 이름 "기본"은 파일에 한국어로 저장된다(다른 앱·구버전과 같은 값을 읽어야 한다).
 그래서 저장값은 그대로 두고 화면에 보일 때만 번역한다. 사용자가 직접 "기본"이라고
 지은 이름(renamed)은 번역하지 않는다.
 */
export function getLayerDisplayName(layer, translate) {
    const name = typeof layer?.name === "string" ? layer.name : ""
    if (!layer?.renamed && name === DEFAULT_LAYER_NAME && typeof translate === "function") {
        return translate(DEFAULT_LAYER_NAME)
    }
    return name
}

/*
 화면에서 레이어를 부르는 번호. 목록에서 위부터 센 자리다(DCW-1931·1933, 2026-10-23 사용자 결정).
 웹 화면은 이름을 보이지 않고 이 번호만 쓴다 - 순서를 바꾸면 번호는 자리에 남고 그림·설명이 옮겨 간다.
 이름(name)은 파일에 그대로 저장해 다른 앱·구버전과 맞춘다(getLayerDisplayName 은 저장할 이름을 지을 때만 쓴다).
 발표 모드와 닷패드 20칸 줄(textLineLabel)도 이 번호다 - 숨긴 레이어도 자리를 차지한다(DCW-1933, QA 10/24).
 예전에는 출력 쪽이 보이는 레이어만 세어, 위 레이어를 숨기면 같은 레이어를 패널과 다른 번호로 불렀다.
 - parameter layers: 번호를 셀 레이어 목록(위에서 아래 순서)
 - returns: {Number} 1부터. 목록에 없으면 0
 */
export function getLayerNumber(layers, layer) {
    const id = typeof layer === "string" ? layer : layer?.id
    return (layers || []).findIndex(item => item?.id === id) + 1
}
function normalizeLayerName(layer, index) {
    const name = typeof layer?.name === "string" ? layer.name.trim() : ""
    // 아래 두 규칙은 구버전 기본 이름("기본 레이어", "레이어 N")을 현재 명명 규칙으로
    // 옮기기 위한 것이다. 사용자가 직접 지은 이름이 우연히 같은 모양이면 안 되므로
    // renameLayer가 표시해 둔 renamed 플래그가 붙은 이름은 건드리지 않는다.
    if (layer?.renamed && name) return name
    if (name === "기본 레이어") return "기본"

    const legacyDefaultMatch = /^레이어\s+(\d+)$/.exec(name)
    if (legacyDefaultMatch) {
        return String(Math.max(1, Number(legacyDefaultMatch[1]) - 1))
    }
    if (name) return name
    return layer?.id === "legacy-raster" ? "기본" : String(index + 1)
}

function getNextLayerName(layers) {
    const largestNumber = layers.reduce((largest, layer) => {
        const name = String(layer?.name || "").trim()
        return /^\d+$/.test(name) ? Math.max(largest, Number(name)) : largest
    }, 0)
    return String(largestNumber + 1)
}

/** 레이어 설명 한 칸에 담는 최대 글자 수. 페이지 설명처럼 20칸 점자로 넘겨 읽는 글이다. */
export const MAX_LAYER_DESCRIPTION_LENGTH = 1000

/*
 레이어 설명. 예전 파일에는 이 값이 없으므로 빈 글로 읽는다.
 줄바꿈은 \n 하나로 맞추고 앞뒤 공백은 지운다 - 공백만 있는 설명은 "설명 없음"과 같다.
 */
export function normalizeLayerDescription(value) {
    if (typeof value !== "string") return ""
    return value.replace(/\r\n?/g, "\n").trim().slice(0, MAX_LAYER_DESCRIPTION_LENGTH)
}

function normalizeLayer(layer, surface, index) {
    return {
        id: typeof layer?.id === "string" && layer.id ? layer.id : createLayerId(),
        name: normalizeLayerName(layer, index),
        renamed: Boolean(layer?.renamed),
        description: normalizeLayerDescription(layer?.description),
        visible: layer?.visible !== false,
        locked: Boolean(layer?.locked),
        blendMode: "or",
        colorIndex: normalizeLayerColorIndex(layer?.colorIndex),
        raster: normalizeRaster(layer?.rasterHex ?? layer?.raster, surface),
        objects: Array.isArray(layer?.objects) ? layer.objects.map(normalizeLayerObject).filter(Boolean) : [],
        stateHistory: Array.isArray(layer?.stateHistory) ? layer.stateHistory : [],
        undoneChanges: Array.isArray(layer?.undoneChanges) ? layer.undoneChanges : [],
    }
}

/*
 레이어 기능을 쓰지 않은 페이지도 저장할 때는 기본 레이어 한 장을 적는다 - 앱이 레이어 칸이
 있는지로 신규 형식을 가르기 때문이다. 그 한 장이 손대지 않은 기본 레이어면 다시 열었을 때도
 "레이어를 쓰지 않은 페이지"로 본다. 그래야 옛 입력 창이 graphic.data 를 고치면 기본 레이어가
 따라간다(syncLegacyPageLayerFromGraphic).
 */
function isPlainBaseLayerExtension(extension) {
    const layers = extension?.layers
    if (!Array.isArray(layers) || layers.length !== 1) return false
    const [layer] = layers
    return layer?.id === "legacy-raster"
        && !layer.renamed
        && normalizeLayerName(layer, 0) === DEFAULT_LAYER_NAME
        && !normalizeLayerDescription(layer.description)
        && layer.visible !== false
        && !layer.locked
        && normalizeLayerColorIndex(layer.colorIndex) === 0
        && !(Array.isArray(layer.objects) && layer.objects.length > 0)
}

/** 파일의 레이어 칸이 이 페이지가 레이어 기능을 쓴다는 뜻인지. */
export function extensionUsesLayerFeature(extension) {
    return READABLE_LAYER_SCHEMA_VERSIONS.has(extension?.schemaVersion)
        && Array.isArray(extension.layers)
        && extension.layers.length > 0
        && !isPlainBaseLayerExtension(extension)
}

/*
 앱이 핀마다 적은 색(graphic.colors)을 파일을 열 때 한 번 찍어 둔다. Page 가 만들어질 때 부른다.
 그때의 그림도 함께 둔다. 그 뒤 graphic.data 가 바뀌어도(간편 모드는 graphic.data 에 바로 그린다)
 처음부터 있던 핀에만 그 색을 쓰려는 것이다. 새로 그린 핀은 검정이다.
 - returns: {{codes: Int8Array, raster: Uint8Array}|null} 색이 없거나 모르는 꼴이면 null
 */
export function captureLegacyColors(graphic, dotPadInfo) {
    if (typeof graphic?.colors !== "string") return null
    try {
        const surface = resolveSurface({graphic, dotPadInfo})
        const codes = decodeLegacyColors(graphic.colors, surface)
        return codes ? {codes, raster: normalizeRaster(graphic.data, surface)} : null
    } catch (error) {
        console.warn("[layerState] 앱 색(graphic.colors)을 읽지 못해 검정으로 엽니다", error)
        return null
    }
}

// Page 는 만들 때 찍어 둔다. 발표 창이 쓰는 파일 JSON 쪽(Page 가 아님)은 여기서 읽는다.
function legacyColorsOf(page) {
    return page.legacyColors !== undefined
        ? page.legacyColors
        : captureLegacyColors(page.graphic, page.dotPadInfo)
}

/*
 앱 색이 실린 페이지를 색마다 레이어 한 장으로 나눈다. 이 편집기는 색을 레이어마다 하나로 다루기 때문이다.
 색은 핀을 나눌 뿐이라 레이어를 합치면 graphic.data 와 핀 하나 다르지 않다.
 첫 색(검정이 있으면 검정)이 기본 레이어가 되고, 나머지는 색 번호(앱 차례: 파랑·노랑·빨강) 차례로 그 아래에 붙는다.
 레이어 id 를 정해 두는 것은, 펼치지 않은 페이지를 저장할 때마다 사본을 다시 나누기 때문이다
 (매번 새 id 면 저장할 때마다 파일이 달라진다).
 - returns: {Object[]|null} 검정뿐이거나 색이 없으면 null(지금까지처럼 기본 레이어 한 장)
 */
function buildLegacyColorLayers(page, surface) {
    const origin = legacyColorsOf(page)
    if (!origin) return null
    const current = normalizeRaster(page.graphic?.data, surface)
    if (origin.codes.length !== current.length || origin.raster.length !== current.length) return null

    const rasters = new Map()
    for (let pinIndex = 0; pinIndex < current.length; pinIndex++) {
        if (!current[pinIndex]) continue
        const code = origin.raster[pinIndex] ? origin.codes[pinIndex] : NO_LEGACY_COLOR
        const colorIndex = Math.max(0, code)
        if (!rasters.has(colorIndex)) rasters.set(colorIndex, new Uint8Array(current.length))
        rasters.get(colorIndex)[pinIndex] = 1
    }
    const colorIndexes = LAYER_COLOR_PALETTE.map(color => color.index).filter(colorIndex => rasters.has(colorIndex))
    if (colorIndexes.length === 0 || (colorIndexes.length === 1 && colorIndexes[0] === 0)) return null

    return colorIndexes.map((colorIndex, order) => normalizeLayer({
        id: order === 0 ? "legacy-raster" : `legacy-color-${LAYER_COLOR_PALETTE[colorIndex].key}`,
        name: order === 0 ? DEFAULT_LAYER_NAME : String(order),
        colorIndex,
        raster: rasters.get(colorIndex),
        ...(order === 0 ? {stateHistory: page.stateHistory, undoneChanges: page.undoneChanges} : {}),
    }, surface, order))
}

function isColorOrder(value) {
    return typeof value === "string" && value.length === 4 && [...value].sort().join("") === "BKRY"
}

/*
 레이어 칸의 colorIndex 가 어느 차례로 적혔는지. colorOrder 칸이 있으면 그것을 따른다.
 없으면 2026-10-22 전 웹이 쓴 칸(웹 차례)이다. 다만 앱 색(graphic.colors)이 함께 실린 페이지는 앱이 쓴 것으로
 보고 앱 차례로 읽는다 - 예전 웹은 graphic.colors 를 쓰지 않았고, 앱은 앱 번호로 쓴다.
 */
function storedLayerColorOrder(extension, page) {
    if (isColorOrder(extension?.colorOrder)) return extension.colorOrder
    const origin = legacyColorsOf(page)
    return origin?.codes.some(code => code !== NO_LEGACY_COLOR) ? LAYER_COLOR_ORDER : LEGACY_WEB_COLOR_ORDER
}

function toCurrentColorIndex(colorIndex, order) {
    if (order === LAYER_COLOR_ORDER) return colorIndex
    return LAYER_COLOR_ORDER.indexOf(order[normalizeLayerColorIndex(colorIndex)])
}

export function ensurePageLayerState(page, fallbackSurface) {
    if (!page) return []
    const surface = resolveSurface(page, fallbackSurface)
    if (Array.isArray(page.editorLayers) && page.editorLayers.length > 0) {
        return page.editorLayers
    }

    const extension = page.extensions?.[LAYER_EXTENSION_KEY]
    // 앱 색은 레이어 칸이 없거나 손대지 않은 기본 레이어 한 장뿐일 때만 쓴다. 레이어를 쓴 칸이면 그 칸이 색까지
    // 들고 있고, 못 읽는 판의 칸은 덮어쓰면 안 된다(serializePageLayerExtension 참고).
    let colorLayers = null
    if (extension == null || (READABLE_LAYER_SCHEMA_VERSIONS.has(extension.schemaVersion) && isPlainBaseLayerExtension(extension))) {
        try {
            colorLayers = buildLegacyColorLayers(page, surface)
        } catch (error) {
            // 색을 못 나눠도 그림은 연다(검정 한 장).
            console.warn("[layerState] 앱 색으로 레이어를 나누지 못했습니다", error)
        }
    }
    const extensionLayers = READABLE_LAYER_SCHEMA_VERSIONS.has(extension?.schemaVersion)
        && Array.isArray(extension.layers)
        ? extension.layers
        : []
    const colorOrder = extensionLayers.length > 0 && !colorLayers ? storedLayerColorOrder(extension, page) : LAYER_COLOR_ORDER
    const layers = colorLayers || (extensionLayers.length > 0
        ? extensionLayers.map((layer, index) => normalizeLayer({
            ...layer,
            colorIndex: toCurrentColorIndex(layer?.colorIndex, colorOrder),
        }, surface, index))
        : [normalizeLayer({
            id: "legacy-raster",
            name: "기본",
            colorIndex: 0,
            raster: page.graphic?.data,
            stateHistory: page.stateHistory,
            undoneChanges: page.undoneChanges,
        }, surface, 0)])

    const activeLayerId = colorLayers ? null : extension?.activeLayerId
    page.editorLayers = layers
    page.activeLayerId = layers.some(layer => layer.id === activeLayerId)
        ? activeLayerId
        : layers[0].id
    page.layerFeatureEnabled = colorLayers ? true : extensionUsesLayerFeature(extension)
    return layers
}

export function getActivePageLayer(page, fallbackSurface) {
    const layers = ensurePageLayerState(page, fallbackSurface)
    return layers.find(layer => layer.id === page.activeLayerId) || layers[0] || null
}

export function syncLegacyPageLayerFromGraphic(page, fallbackSurface) {
    const surface = resolveSurface(page, fallbackSurface)
    const layers = ensurePageLayerState(page, surface)
    const legacyLayer = layers.length === 1 ? layers[0] : null
    if (
        !legacyLayer
        || page.layerFeatureEnabled
        || legacyLayer.id !== "legacy-raster"
        || legacyLayer.objects.length > 0
    ) {
        return legacyLayer
    }

    // 기존 입력 모달과 썸네일은 page.graphic.data를 갱신한다. 레이어 기능을 한 번도
    // 사용하지 않은 페이지에서는 이 값을 원본으로 삼아 빈/오래된 기본 레이어를 복구한다.
    legacyLayer.raster = normalizeRaster(page.graphic?.data, surface)
    return legacyLayer
}

export function setActivePageLayer(page, layerId, fallbackSurface) {
    const layers = ensurePageLayerState(page, fallbackSurface)
    if (!layers.some(layer => layer.id === layerId)) return null
    page.activeLayerId = layerId
    return getActivePageLayer(page, fallbackSurface)
}

export function syncActiveLayerRaster(page, pixels, fallbackSurface) {
    const surface = resolveSurface(page, fallbackSurface)
    const activeLayer = getActivePageLayer(page, surface)
    if (!activeLayer || !pixels) return activeLayer
    activeLayer.raster = normalizeRaster(pixels, surface)
    return activeLayer
}

/**
 저장·썸네일이 읽는 page.graphic.data 모양(불리언 배열)으로 옮겨 담는다.
 Array.from(값변환) 은 이터레이터를 거쳐 같은 일을 여러 배 느리게 하는데, 이 변환은
 획 하나 뗄 때마다 지나가는 자리다. 담기는 값은 이전과 같은 true/false 다.
 - returns: {Boolean[]}
 */
export function toGraphicData(source) {
    const length = source?.length || 0
    const data = new Array(length)
    for (let index = 0; index < length; index++) {
        data[index] = Boolean(source[index])
    }
    return data
}

/**
 페이지가 저장해야 할 그림 한 벌.
 레이어를 쓰는 페이지는 보이는 레이어를 모두 합치고, 아직 레이어가 없는 페이지는
 편집 중인 픽셀을 그대로 쓴다(레이어 도입 전 동작).
 pixels 는 Vue 프록시가 벗겨진 원본을 받는다 - 6144 칸을 프록시로 훑으면 그만큼 느려진다.
 - returns: {Boolean[]}
 */
export function buildPageGraphicData(page, pixels, fallbackSurface) {
    return toGraphicData(page?.editorLayers ? composePageLayers(page, fallbackSurface) : pixels)
}

/**
 이동 도구로 집은 개체를 원래 자리에 넣은 페이지(그릴 때만 쓰는 사본).
 집은 개체는 옮기는 동안 지금 레이어에서 빠져 있다(편집기 placeTactileObject - 캔버스에 두 자리로 그려지지 않게).
 그 사이 페이지 그림(page.graphic.data)을 다시 합성하면 왼쪽 페이지 썸네일에서 그 개체가 사라졌다(DCW-1904, QA 10/28
 NEW-G3-4). 확정·취소 전까지 썸네일·레이어 줄 그림은 원래 자리에 넣어 그린다.
 - parameter placement: 편집기 tactilePlacement. 원본 개체가 없으면(새 그림·붙여넣기) page 를 그대로 돌려준다.
 - returns: {Object} page 또는 지금 레이어의 objects 만 바꾼 얕은 사본. page 는 고치지 않는다.
 */
export function withLiftedObject(page, placement) {
    const original = placement?.originalObject
    if (!page || !original || placement.clipboard || !Array.isArray(page.editorLayers)) return page
    const layerIndex = page.editorLayers.findIndex(layer => layer?.id === page.activeLayerId)
    const layer = page.editorLayers[layerIndex]
    if (!layer || (layer.objects || []).some(object => object?.id === original.id)) return page
    const objects = [...(layer.objects || [])]
    const index = Number.isInteger(placement.originalObjectIndex) && placement.originalObjectIndex >= 0
        ? Math.min(placement.originalObjectIndex, objects.length)
        : objects.length
    objects.splice(index, 0, original)
    const editorLayers = [...page.editorLayers]
    editorLayers[layerIndex] = {...layer, objects}
    return {...page, editorLayers}
}

/**
 레이어 구성이 바뀐 뒤(삭제·숨기기·합치기) page.graphic.data 를 다시 합성한다.
 페이지 썸네일은 page.graphic.data 가 "새 배열로 바뀔 때"만 다시 그린다. 레이어를 지우는 쪽은
 지우기 전에 commitCurrentLayerState 로 합성해 두므로, 다시 합성하지 않으면 다음 획까지
 지운 레이어가 썸네일에 남는다(DCW-1780). 제자리 수정 없이 늘 새 배열로 바꾼다.
 - returns: {Boolean[]|null}
 */
export function refreshPageGraphicData(page, fallbackSurface) {
    if (!page) return null
    const data = toGraphicData(composePageLayers(page, fallbackSurface))
    page.graphic = {
        ...(page.graphic || {}),
        data,
    }
    return data
}

// includeLayerIds 는 닷패드 출력에서 "이번에 내보낼 레이어"만 골라내기 위한 것이다.
// 화면 표시(visible)와는 별개의 임시 선택이므로 저장 경로(makeJson)에서는 넘기지 않는다.
// 빈 배열은 "아무 레이어도 내보내지 않음"이고, 생략/null 이면 보이는 레이어 전부를 합성한다.
export function composePageLayers(page, fallbackSurface, {excludeLayerId, includeLayerIds} = {}) {
    const surface = resolveSurface(page, fallbackSurface)
    const composite = new Uint8Array(surface.width * surface.height)
    const layers = ensurePageLayerState(page, surface)
    const includeFilter = Array.isArray(includeLayerIds) ? new Set(includeLayerIds) : null
    layers.forEach(layer => {
        if (!layer.visible || layer.id === excludeLayerId) return
        if (includeFilter && !includeFilter.has(layer.id)) return
        const raster = composeLayerRaster(layer, surface)
        for (let index = 0; index < composite.length; index++) {
            if (raster[index]) composite[index] = 1
        }
    })
    return composite
}

export function addPageLayer(page, fallbackSurface, options = {}) {
    const surface = resolveSurface(page, fallbackSurface)
    const layers = ensurePageLayerState(page, surface)
    if (layers.length >= MAX_PAGE_LAYERS) return null

    const activeLayer = getActivePageLayer(page, surface)

    const layer = normalizeLayer({
        id: createLayerId(),
        name: options.name || getNextLayerName(layers),
        colorIndex: options.colorIndex ?? activeLayer?.colorIndex ?? 0,
        raster: new Uint8Array(surface.width * surface.height),
    }, surface, layers.length)
    // 배열 뒤쪽이 패널에서 아래쪽이다. 새 레이어는 늘 맨 아래에 붙어야
    // 기본 → 1 → 2 → 3 순으로 쌓여 만든 순서대로 출력을 얹어 갈 수 있다.
    // 활성 레이어 옆에 끼워 넣으면 목록 중간을 고른 채 누를 때마다 새 레이어가
    // 이미 만들어 둔 레이어들보다 위로 올라가 순서가 뒤집힌다.
    layers.push(layer)
    page.activeLayerId = layer.id
    page.layerFeatureEnabled = true
    return layer
}

/**
 객체 종류에 맞는 id 앞가지. 새 id 를 발급할 때 종류를 알아볼 수 있게 붙인다.
 - returns: {String}
 */
export function resolveEditorObjectIdPrefix(object) {
    if (object?.type === "brailleMath") return "math"
    if (object?.type === "brailleText") return "text"
    if (object?.sourceKind === "shape") return "shape"
    if (object?.sourceKind === "stroke") return "stroke"
    return "tactile-graphic"
}

/**
 레이어를 한 장 그대로 더 만든다. 사본은 원본 바로 아래에 놓고 활성 레이어로 삼는다.
 점과 객체는 값으로 옮겨 담아 원본과 실을 끊는다 - 한쪽을 고쳤을 때 다른 쪽이 따라 바뀌면
 복제가 아니라 같은 레이어를 두 번 보여 주는 것이 된다.
 options.cloneObject 로 더 깊은 복제기를 받는다. 없으면 바이트 배열까지만 새로 뜬다.
 - returns: {Object|null} 만들어진 레이어. 최대 개수를 넘었거나 원본이 없으면 null.
 */
export function duplicatePageLayer(page, layerId, fallbackSurface, options = {}) {
    const surface = resolveSurface(page, fallbackSurface)
    const layers = ensurePageLayerState(page, surface)
    if (layers.length >= MAX_PAGE_LAYERS) return null
    const index = layers.findIndex(layer => layer.id === layerId)
    if (index < 0) return null

    const source = layers[index]
    const cloneObject = typeof options.cloneObject === "function" ? options.cloneObject : normalizeLayerObject
    const layer = normalizeLayer({
        ...source,
        id: createLayerId(),
        name: options.name || getNextLayerName(layers),
        // 직접 지은 이름의 사본만 "직접 지은 이름"으로 남긴다. 호출부가 이름을 넘겼다는 것만으로는
        // 사용자가 지은 이름이 아니다 - "1 사본" 처럼 원본 이름에서 기계적으로 만든 이름도 있다.
        renamed: Boolean(source.renamed),
        // 잠긴 레이어도 사본은 바로 그릴 수 있어야 한다. 잠금은 원본을 지키려는 것이지
        // 사본까지 묶어 두려는 것이 아니다.
        locked: false,
        raster: source.raster,
        objects: (source.objects || []).map(object => {
            const copy = cloneObject(object)
            // id 를 물려받으면 두 레이어의 객체가 같은 것으로 취급되어 한쪽 편집이 양쪽에 남는다.
            if (copy) copy.id = createEditorObjectId(resolveEditorObjectIdPrefix(copy))
            return copy
        }).filter(Boolean),
        // 되돌리기 기록은 원본의 것이다. 사본이 물려받으면 남의 이력을 되돌리게 된다.
        stateHistory: [],
        undoneChanges: [],
    }, surface, index)

    /*
     배열 뒤쪽이 패널에서 아래다. 사본은 원본 바로 "아래"에 놓는다.
     그림판 관습은 사본을 원본 위에 얹지만, 여기서는 순차 출력이 맨 위에서 아래로
     한 장씩 얹히므로 위에 놓으면 사본이 원본보다 먼저 나가 순서가 뒤집힌다.
     새 레이어를 맨 아래에 붙이는 addPageLayer 와 방향을 맞춰 둔다.
     */
    layers.splice(index + 1, 0, layer)
    page.activeLayerId = layer.id
    page.layerFeatureEnabled = true
    return layer
}

/*
 오디오 타이밍 시점이 가리키는 레이어 id 를 바꾼다(next 가 null 이면 뺀다). 바뀐 시점은 확인을 요청한다.
 없는 레이어를 가리키는 시점이 남으면 재생기가 그 자리에서 "잘못된 시점"으로 소리까지 멈춘다.
 이 편집기에는 레이어 삭제·합치기 되돌리기가 없어 그런 참조는 영영 고쳐지지 않는다.
 */
function replaceAudioCueLayer(page, layerId, next) {
    const cues = page.audio?.layerCues
    if (!Array.isArray(cues) || cues.length === 0) return
    page.audio.layerCues = cues.map(cue => {
        if (!cue.layerIds?.includes(layerId)) return cue
        const layerIds = [...new Set(cue.layerIds.flatMap(id => id === layerId ? (next ? [next] : []) : [id]))]
        return {...cue, layerIds, needsReview: true}
    })
}

export function removePageLayer(page, layerId, fallbackSurface) {
    const layers = ensurePageLayerState(page, fallbackSurface)
    if (layers.length <= 1) return null
    const index = layers.findIndex(layer => layer.id === layerId)
    if (index < 0) return null

    const [removed] = layers.splice(index, 1)
    if (page.activeLayerId === layerId) {
        page.activeLayerId = layers[Math.min(index, layers.length - 1)].id
    }
    page.layerFeatureEnabled = true
    // 지운 레이어의 그림은 어디에도 없으니 시점에서도 뺀다.
    replaceAudioCueLayer(page, layerId, null)
    return removed
}

function cloneAudioLayerCues(cues) {
    return Array.isArray(cues) ? cues.map(cue => ({...cue, layerIds: [...(cue.layerIds || [])]})) : null
}

/**
 여러 레이어를 한 번에 지운다(DCW-1779). 한 장은 반드시 남긴다 - 전부 고르면 아무것도 지우지 않는다.
 레이어마다 removePageLayer 를 거치므로 편집 대상 이동과 오디오 타이밍 시점 정리 규칙이 한 장씩 지울 때와 같다.
 되돌리기에 쓸 기록을 돌려준다(지운 레이어 객체와 원래 자리, 지우기 전·후의 시점).
 - returns: {Object|null} 지운 것이 없으면 null
 */
export function removePageLayers(page, layerIds, fallbackSurface) {
    const layers = ensurePageLayerState(page, fallbackSurface)
    const targets = new Set((layerIds || []).filter(id => layers.some(layer => layer.id === id)))
    if (targets.size === 0 || targets.size >= layers.length) return null

    const record = {
        activeLayerId: page.activeLayerId,
        timingId: page.audio?.timingId ?? null,
        layerCuesBefore: cloneAudioLayerCues(page.audio?.layerCues),
        removed: layers
            .map((layer, index) => ({layer, index}))
            .filter(({layer}) => targets.has(layer.id)),
    }
    for (const {layer} of record.removed) removePageLayer(page, layer.id, fallbackSurface)
    record.layerCuesAfter = cloneAudioLayerCues(page.audio?.layerCues)
    return record
}

/**
 레이어 패널의 "모든 레이어 지우기"에서 남길 레이어와 지울 레이어.
 기본 레이어 한 장만 남긴다 - 이름을 바꾸지 않은 "기본" 레이어, 없으면(이름을 바꿨거나 지웠으면) 목록 맨 위 레이어다.
 잠긴 레이어는 지우지 않는다(잠금은 지키려고 건 것이다).
 - returns: {{keeperId: String, removeIds: String[]}|null} 레이어가 없으면 null
 */
export function planPageLayerReset(layers) {
    const list = layers || []
    if (list.length === 0) return null
    const keeper = list.find(layer => !layer?.renamed && layer?.name === DEFAULT_LAYER_NAME) || list[0]
    return {
        keeperId: keeper.id,
        removeIds: list.filter(layer => layer !== keeper && !layer?.locked).map(layer => layer.id),
    }
}

/**
 "모든 레이어 지우기"로 바뀌는 것이 있는지. 지울 레이어가 있거나, 남길 레이어(잠기지 않은)에 그림이 있거나
 숨겨져 있으면 true 다. hasInk(layer) 로 그림이 있는지 본다 - 패널은 줄 미리보기 점을, 편집기는 레이어 자체를 본다.
 - returns: {Boolean}
 */
export function hasPageLayerResetWork(layers, hasInk) {
    const plan = planPageLayerReset(layers)
    if (!plan) return false
    if (plan.removeIds.length > 0) return true
    const keeper = layers.find(layer => layer.id === plan.keeperId)
    if (!keeper || keeper.locked) return false
    return !keeper.visible || Boolean(hasInk(keeper))
}

/**
 removePageLayers 의 되돌리기. 지운 레이어를 원래 자리에 그대로(같은 id·그림·객체·되돌리기 기록) 되돌리고
 오디오 타이밍 시점도 지우기 전으로 돌린다. 그 사이 오디오를 바꿨으면(timingId 가 다르면) 시점은 건드리지 않는다.
 편집 대상은 바꾸지 않는다 - 되돌리기 기록이 지금 편집 대상의 기록에 있어서, 바꾸면 다시 실행할 길이 끊긴다.
 - returns: {Boolean} 전부 되돌렸으면 true. 충돌·공간 부족이면 아무것도 바꾸지 않는다.
 */
export function restorePageLayers(page, record, fallbackSurface) {
    if (!record?.removed?.length) return false
    const layers = ensurePageLayerState(page, fallbackSurface)
    const removedIds = record.removed.map(entry => entry.layer?.id)
    if (layers.length + removedIds.length > MAX_PAGE_LAYERS ||
        removedIds.some(id => !id || layers.some(layer => layer.id === id)) ||
        new Set(removedIds).size !== removedIds.length) return false
    for (const {layer, index} of [...record.removed].sort((a, b) => a.index - b.index)) {
        layers.splice(Math.max(0, Math.min(index, layers.length)), 0, layer)
    }
    if (page.audio && record.layerCuesBefore && (page.audio.timingId ?? null) === record.timingId) {
        page.audio.layerCues = cloneAudioLayerCues(record.layerCuesBefore)
    }
    page.layerFeatureEnabled = true
    return true
}

/**
 레이어 설명을 바꾼다(DCW-1791).
 - returns: {Object|null} 바꾼 레이어. 없는 레이어면 null
 */
export function setPageLayerDescription(page, layerId, description, fallbackSurface) {
    const layer = ensurePageLayerState(page, fallbackSurface).find(item => item.id === layerId)
    if (!layer) return null
    layer.description = normalizeLayerDescription(description)
    page.layerFeatureEnabled = true
    return layer
}

// 대상 레이어가 바로 아래 레이어를 흡수한다. 이름·색·잠금·표시는 대상 것을 그대로 두므로
// "기본"과 "1"을 합치면 결과가 "기본"으로 남는다. 점은 OR 로 겹치고 객체는 이어 붙여서
// (구워 버리지 않고) 합친 뒤에도 글자·수식·촉각 객체를 그대로 편집할 수 있다.
export function mergePageLayerDown(page, layerId, fallbackSurface) {
    const surface = resolveSurface(page, fallbackSurface)
    const layers = ensurePageLayerState(page, surface)
    const index = layers.findIndex(layer => layer.id === layerId)
    if (index < 0 || index >= layers.length - 1) return null

    const target = layers[index]
    const below = layers[index + 1]
    if (target.locked || below.locked) return null

    const merged = new Uint8Array(surface.width * surface.height)
    for (let pinIndex = 0; pinIndex < merged.length; pinIndex++) {
        merged[pinIndex] = target.raster[pinIndex] || below.raster[pinIndex] ? 1 : 0
    }
    target.raster = merged
    target.objects = [...(target.objects || []), ...(below.objects || [])]
    // 아래 레이어의 그림이 대상 안으로 들어오므로 그 설명도 버리지 않고 이어 붙인다.
    target.description = normalizeLayerDescription(
        [target.description, below.description].filter(text => text && text.trim()).join("\n")
    )

    layers.splice(index + 1, 1)
    if (page.activeLayerId === below.id) page.activeLayerId = target.id
    page.layerFeatureEnabled = true
    // 아래 레이어의 그림은 이제 대상 안에 있다. 대상의 원래 그림까지 함께 나가므로 확인을 요청한다.
    replaceAudioCueLayer(page, below.id, target.id)
    return target
}

function snapshotMergeTarget(layer) {
    return {raster: Uint8Array.from(layer.raster), objects: [...(layer.objects || [])], description: layer.description || ""}
}

/**
 고른 레이어 여러 장을 맨 위의 것 한 장으로 합친다(여러 레이어 선택). 아래로 합치기를 여러 장으로 넓힌 것이라
 규칙이 같다 - 이름·색·잠금·표시는 맨 위 레이어 것, 점은 OR, 객체는 위에서부터 이어 붙이고(굽지 않는다),
 설명도 이어 붙인다. 사이에 끼인 고르지 않은 레이어는 그 자리에 그대로 둔다.
 잠긴 레이어가 하나라도 있으면 합치지 않는다.
 되돌리기에 쓸 기록을 돌려준다(맨 위 레이어의 합치기 전 그림, 합쳐 사라진 레이어와 원래 자리, 시점).
 - returns: {Object|null} 합친 것이 없으면 null
 */
export function mergePageLayers(page, layerIds, fallbackSurface) {
    const surface = resolveSurface(page, fallbackSurface)
    const layers = ensurePageLayerState(page, surface)
    const ids = new Set(layerIds || [])
    const picked = layers.filter(layer => ids.has(layer.id))
    if (picked.length < 2 || picked.some(layer => layer.locked)) return null

    const [target, ...rest] = picked
    const record = {
        targetId: target.id,
        targetBefore: snapshotMergeTarget(target),
        activeLayerId: page.activeLayerId,
        timingId: page.audio?.timingId ?? null,
        layerCuesBefore: cloneAudioLayerCues(page.audio?.layerCues),
        removed: rest.map(layer => ({layer, index: layers.indexOf(layer)})),
    }

    const merged = Uint8Array.from(target.raster)
    for (const layer of rest) {
        for (let pinIndex = 0; pinIndex < merged.length; pinIndex++) {
            if (layer.raster[pinIndex]) merged[pinIndex] = 1
        }
    }
    target.raster = merged
    target.objects = [...(target.objects || []), ...rest.flatMap(layer => layer.objects || [])]
    target.description = normalizeLayerDescription(
        picked.map(layer => layer.description).filter(text => text && text.trim()).join("\n")
    )

    for (const layer of rest) {
        layers.splice(layers.indexOf(layer), 1)
        replaceAudioCueLayer(page, layer.id, target.id)
    }
    if (rest.some(layer => layer.id === page.activeLayerId)) page.activeLayerId = target.id
    page.layerFeatureEnabled = true
    record.layerCuesAfter = cloneAudioLayerCues(page.audio?.layerCues)
    return record
}

/**
 mergePageLayers 의 되돌리기. 맨 위 레이어를 합치기 전 그림으로 돌리고 합쳐 사라진 레이어를 원래 자리에
 그대로(같은 id·그림·객체·되돌리기 기록) 되돌린다. 시점은 그 사이 오디오를 바꾸지 않았을 때만 돌린다.
 편집 대상은 바꾸지 않는다(restorePageLayers 와 같은 이유).
 - returns: {Boolean} 되돌렸으면 true. 맨 위 레이어가 없거나 자리가 없으면 아무것도 바꾸지 않는다.
 */
export function restoreMergedPageLayers(page, record, fallbackSurface) {
    if (!record?.removed?.length || !record.targetBefore) return false
    const layers = ensurePageLayerState(page, fallbackSurface)
    const target = layers.find(layer => layer.id === record.targetId)
    const removedIds = record.removed.map(entry => entry.layer?.id)
    if (!target || layers.length + removedIds.length > MAX_PAGE_LAYERS ||
        removedIds.some(id => !id || layers.some(layer => layer.id === id))) return false

    target.raster = Uint8Array.from(record.targetBefore.raster)
    target.objects = [...record.targetBefore.objects]
    target.description = record.targetBefore.description
    for (const {layer, index} of [...record.removed].sort((a, b) => a.index - b.index)) {
        layers.splice(Math.max(0, Math.min(index, layers.length)), 0, layer)
    }
    if (page.audio && record.layerCuesBefore && (page.audio.timingId ?? null) === record.timingId) {
        page.audio.layerCues = cloneAudioLayerCues(record.layerCuesBefore)
    }
    page.layerFeatureEnabled = true
    return true
}

export function movePageLayer(page, layerId, direction, fallbackSurface) {
    const layers = ensurePageLayerState(page, fallbackSurface)
    const index = layers.findIndex(layer => layer.id === layerId)
    const targetIndex = index + direction
    if (index < 0 || targetIndex < 0 || targetIndex >= layers.length) return false
    return reorderPageLayer(page, layerId, targetIndex, fallbackSurface)
}

/**
 끌어서 놓은 경계(행과 행 사이)를 옮겨 갈 자리로 바꾼다.
 경계는 끌던 레이어가 아직 목록에 있을 때의 자리이므로, 자기보다 아래에 놓으면
 자기가 빠지면서 한 칸 당겨진다. 이 한 칸을 빼먹으면 아래로 끌 때마다 한 칸씩 덜 간다.
 - returns: {Number} 옮겨 갈 자리. 순서가 그대로면 fromIndex 를 그대로 돌려준다.
 */
export function resolveLayerDropIndex(fromIndex, boundaryIndex) {
    return boundaryIndex > fromIndex ? boundaryIndex - 1 : boundaryIndex
}

/**
 레이어를 목표 자리로 옮긴다.
 끌어서 놓는 자리는 방향(±1)이 아니라 인덱스라서 movePageLayer 로는 표현되지 않는다.
 - returns: {Boolean} 순서가 실제로 바뀌었는지
 */
export function reorderPageLayer(page, layerId, toIndex, fallbackSurface) {
    const layers = ensurePageLayerState(page, fallbackSurface)
    const index = layers.findIndex(layer => layer.id === layerId)
    if (index < 0) return false
    const targetIndex = Math.max(0, Math.min(layers.length - 1, Math.trunc(toIndex)))
    if (!Number.isFinite(targetIndex) || targetIndex === index) return false
    const [layer] = layers.splice(index, 1)
    layers.splice(targetIndex, 0, layer)
    page.layerFeatureEnabled = true
    return true
}

// 레이어 기능을 쓰지 않은 페이지도 기본 레이어 한 장으로 적는다(isPlainBaseLayerExtension 참고).
export function serializePageLayerExtension(page, fallbackSurface) {
    if (!page) return null
    const surface = resolveSurface(page, fallbackSurface)
    // 편집기에 한 번도 펼치지 않은 페이지(간편 모드, 넘겨 보지 않은 페이지)는 사본으로 읽고
    // page 에는 레이어를 만들어 두지 않는다. 만들어 두면 다음 makeJson 이 graphic.data 대신
    // 그 사본을 합성해, 그 사이 graphic.data 에 그린 것이 저장되지 않는다.
    const source = Array.isArray(page.editorLayers) && page.editorLayers.length > 0
        ? page
        : {...page, editorLayers: null, activeLayerId: null}
    const layers = ensurePageLayerState(source, surface)
    // 이 편집기가 못 읽는 판(앞으로 생길 판)의 레이어 칸은 여기서 레이어를 손대지 않은 한
    // 그대로 둔다. 기본 레이어 한 장으로 덮으면 그 판의 내용이 사라진다.
    const stored = page.extensions?.[LAYER_EXTENSION_KEY]
    if (stored && !READABLE_LAYER_SCHEMA_VERSIONS.has(stored.schemaVersion) && !source.layerFeatureEnabled) {
        return null
    }
    const serializedLayers = layers.map(layer => ({
        id: layer.id,
        name: layer.name,
        ...(layer.renamed ? {renamed: true} : {}),
        // 설명이 없는 레이어는 예전과 같은 모양으로 적는다(옛 앱은 모르는 칸을 그냥 건너뛴다).
        ...(normalizeLayerDescription(layer.description) ? {description: normalizeLayerDescription(layer.description)} : {}),
        visible: layer.visible,
        locked: layer.locked,
        blendMode: "or",
        colorIndex: normalizeLayerColorIndex(layer.colorIndex),
        rasterHex: encodeLegacyRaster(layer.raster, surface),
        objects: (layer.objects || []).map(serializeLayerObject),
    }))

    // pinsHex 를 모르는 옛 앱이 이 확장을 반쯤 읽으면 그 객체만 빈 그림이 되어, 사용자에게는
    // 획이 사라진 것으로 보인다. 판(schemaVersion)을 올려 두면 옛 앱은 확장을 통째로 건너뛰고
    // v1 graphic 그림을 열어 - 그림은 온전하고 레이어·객체 편집만 안 된다.
    // 그래서 판은 실제로 pinsHex 를 쓴 페이지에서만 올린다.
    const usesPinIndexList = serializedLayers.some(
        layer => layer.objects.some(object => typeof object?.pinsHex === "string")
    )

    return {
        schemaVersion: usesPinIndexList ? LAYER_SCHEMA_VERSION_PIN_INDEX : LAYER_SCHEMA_VERSION_BASE,
        // colorIndex 를 앱 번호로 적었다는 표시. 이 칸이 없는 예전 파일은 열 때 웹 차례로 읽는다.
        // 앱(iOS·Android)은 모르는 칸을 건너뛰고 colorIndex 를 앱 번호로 읽는다.
        colorOrder: LAYER_COLOR_ORDER,
        activeLayerId: source.activeLayerId,
        layers: serializedLayers,
    }
}

/*
 저장할 그림(graphicData)의 핀마다 색을 앱 꼴(graphic.colors)로 적는다. Page.makeJson 이 부른다.
 핀 색은 그 핀을 가진 맨 위 보이는 레이어의 색이다(화면도 위 레이어 색이 보인다).
 - 앱 색 없이 연 페이지가 전부 검정이면 적지 않는다 - 지금까지와 같은 파일이다.
 - 앱은 60x40 만 연다. 다른 크기는 레이어 칸의 colorIndex 에만 색을 남긴다.
   앱이 모르는 길이의 colors 를 어떻게 다룰지 알 수 없어서다.
 - 앱이 색 없이('0') 둔 올라온 핀은 그대로 검정이면 '0' 으로 되돌려 적는다(앱 파일을 손대지 않고
   다시 저장하면 colors 가 글자 하나 다르지 않다).
 - 실패해도 저장은 막지 않는다(색만 빠진다, 이전과 같은 파일).
 - returns: {String|null}
 */
export function serializeLegacyColors(page, graphicData, fallbackSurface) {
    if (!page) return null
    try {
        const surface = resolveSurface(page, fallbackSurface)
        if (surface.width !== LEGACY_COLOR_SURFACE.width || surface.height !== LEGACY_COLOR_SURFACE.height) return null
        const pinCount = surface.width * surface.height
        const origin = legacyColorsOf(page)
        const keepsOrigin = Boolean(origin) && origin.codes.length === pinCount && origin.raster.length === pinCount
        // 펼치지 않은 페이지는 사본으로 읽는다(serializePageLayerExtension 과 같은 까닭).
        const source = Array.isArray(page.editorLayers) && page.editorLayers.length > 0
            ? page
            : {...page, editorLayers: null, activeLayerId: null}
        const layers = ensurePageLayerState(source, surface).filter(layer => layer.visible)
        // 대부분의 페이지(앱 색 없이 연 검정 레이어뿐인 페이지)는 여기서 끝난다.
        if (!keepsOrigin && layers.every(layer => normalizeLayerColorIndex(layer.colorIndex) === 0)) return null

        const pins = normalizeRaster(graphicData, surface)
        const codes = new Int8Array(pinCount).fill(NO_LEGACY_COLOR)
        // 배열 앞쪽이 위 레이어다. 먼저 칠한 색을 아래 레이어가 덮지 않는다.
        layers.forEach(layer => {
            const colorIndex = normalizeLayerColorIndex(layer.colorIndex)
            const raster = composeLayerRaster(layer, surface)
            for (let pinIndex = 0; pinIndex < pinCount; pinIndex++) {
                if (pins[pinIndex] && raster[pinIndex] && codes[pinIndex] === NO_LEGACY_COLOR) codes[pinIndex] = colorIndex
            }
        })

        let hasColor = false
        for (let pinIndex = 0; pinIndex < pinCount; pinIndex++) {
            if (!pins[pinIndex]) continue
            // 그림에는 있는데 레이어에서 못 찾은 핀(펼치지 않은 페이지의 낡은 레이어 칸)은 검정이다.
            if (codes[pinIndex] === NO_LEGACY_COLOR) codes[pinIndex] = 0
            if (codes[pinIndex] > 0) {
                hasColor = true
            } else if (keepsOrigin && origin.raster[pinIndex] && origin.codes[pinIndex] === NO_LEGACY_COLOR) {
                codes[pinIndex] = NO_LEGACY_COLOR
            }
        }
        if (!hasColor && !keepsOrigin) return null
        return encodeLegacyColors(codes, surface)
    } catch (error) {
        console.warn("[layerState] 앱 색(graphic.colors)을 적지 못했습니다 - 색 없이 저장합니다", error)
        return null
    }
}

/*
 그릴 핀마다 보이는 색 번호(LAYER_COLOR_PALETTE)를 구한다. 썸네일·발표 화면이 핀을 그 색으로 칠할 때 쓴다.
 예전에는 썸네일을 그리는 곳이 모두 켜짐/꺼짐(graphic.data)만 읽고 검정으로 칠해, 색 펜으로 그린 그림이
 편집 화면·발표 모드·클라우드 썸네일에서 검정으로만 보였다(DCW-1944·1945·1946).
 색은 serializeLegacyColors 와 같이 그 핀을 가진 맨 위 보이는 레이어의 색이다. 60x40 이 아닌 면(768)도 된다.
 파일 JSON(발표·클라우드 목록)도 받는다. 펼치지 않은 페이지는 사본으로 읽어 원본에 editorLayers 를 붙이지 않는다.
 - param pixels: 그릴 켜진 핀(graphic.data 를 편 것). 레이어에서 못 찾은 핀은 검정이다.
 - param includeLayerIds: 발표 모드 레이어 단계처럼 일부 레이어만 그릴 때 그 레이어들
 - returns: {Int8Array|null} 핀마다 색 번호. 모두 검정이면 null(지금처럼 검정 한 색으로 그린다)
 */
export function resolvePinColorIndexes(page, pixels, {fallbackSurface, includeLayerIds} = {}) {
    if (!page || !pixels) return null
    try {
        const surface = resolveSurface(page, fallbackSurface)
        const pinCount = surface.width * surface.height
        if (pixels.length !== pinCount) return null
        const source = Array.isArray(page.editorLayers) && page.editorLayers.length > 0
            ? page
            : {...page, editorLayers: null, activeLayerId: null}
        const includeFilter = Array.isArray(includeLayerIds) ? new Set(includeLayerIds) : null
        const layers = ensurePageLayerState(source, surface)
            .filter(layer => layer.visible && (!includeFilter || includeFilter.has(layer.id)))
        if (layers.every(layer => normalizeLayerColorIndex(layer.colorIndex) === 0)) return null

        const colors = new Int8Array(pinCount)
        const painted = new Uint8Array(pinCount)
        // 배열 앞쪽이 위 레이어다. 먼저 칠한 색을 아래 레이어가 덮지 않는다.
        layers.forEach(layer => {
            const colorIndex = normalizeLayerColorIndex(layer.colorIndex)
            const raster = composeLayerRaster(layer, surface)
            for (let pinIndex = 0; pinIndex < pinCount; pinIndex++) {
                if (!pixels[pinIndex] || !raster[pinIndex] || painted[pinIndex]) continue
                colors[pinIndex] = colorIndex
                painted[pinIndex] = 1
            }
        })
        return colors
    } catch (error) {
        // 색을 못 구해도 그림은 그린다(검정).
        console.warn("[layerState] 핀 색을 구하지 못해 검정으로 그립니다", error)
        return null
    }
}

export function clonePageLayerState(sourcePage, targetPage, fallbackSurface) {
    if (!Array.isArray(sourcePage?.editorLayers)) return targetPage
    const surface = resolveSurface(sourcePage, fallbackSurface)
    targetPage.editorLayers = sourcePage.editorLayers.map((layer, index) => normalizeLayer({
        ...layer,
        id: index === 0 && layer.id === "legacy-raster" ? "legacy-raster" : createLayerId(),
        raster: layer.raster,
        stateHistory: [],
        undoneChanges: [],
    }, surface, index))
    const sourceActiveIndex = Math.max(0, sourcePage.editorLayers.findIndex(
        layer => layer.id === sourcePage.activeLayerId
    ))
    targetPage.activeLayerId = targetPage.editorLayers[sourceActiveIndex]?.id || targetPage.editorLayers[0].id
    targetPage.layerFeatureEnabled = Boolean(sourcePage.layerFeatureEnabled)
    if (targetPage.audio?.layerCues?.length) {
        const ids = new Map(sourcePage.editorLayers.map((layer, index) => [layer.id, targetPage.editorLayers[index].id]))
        targetPage.audio.layerCues = targetPage.audio.layerCues.map(cue => ({...cue, layerIds: cue.layerIds.map(id => ids.get(id) || id)}))
    }
    return targetPage
}
